import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

const ACTION_LABELS = {
  project_created: 'created',
  project_updated: 'updated',
  project_deleted: 'deleted',
  project_duplicated: 'duplicated',
  board_created: 'Board created',
};

const FIELD_LABELS = {
  rag: 'Status',
  name: 'Name',
  phase: 'Phase',
  owner: 'Owner',
  pct: '% Complete',
  latest_update: 'Latest Update',
  workstream_id: 'Workstream',
};

export default function ActivityPage() {
  const { boardId } = useParams();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const { authedFetch } = useAuth();

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const data = await authedFetch(`/api/boards/${boardId}/activity?limit=100`);
        setActivities(data);
      } catch (e) {
        console.error('Failed to fetch activity:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchActivity();
  }, [boardId, authedFetch]);

  // Group by date
  const grouped = activities.reduce((acc, a) => {
    const date = new Date(a.created_at).toLocaleDateString();
    if (!acc[date]) acc[date] = [];
    acc[date].push(a);
    return acc;
  }, {});

  return (
    <div className="px-6 py-8 max-w-3xl">
      <div className="flex items-center gap-3 mb-8">
        <Link to={`/boards/${boardId}`}>
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight">Activity Log</h1>
          <p className="text-sm text-muted-foreground">
            {activities.length} event{activities.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Loading activity...</p>
      ) : activities.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center py-12">
            <Clock className="h-10 w-10 text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">No activity yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([date, items]) => (
            <div key={date}>
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">{date}</h3>
              <div className="space-y-2">
                {items.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-start gap-3 rounded-lg border p-3 text-sm"
                    data-testid={`activity-item-${a.id}`}
                  >
                    <div className="h-2 w-2 rounded-full bg-[hsl(var(--ring))] mt-1.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-xs">
                          {ACTION_LABELS[a.action] || a.action}
                        </Badge>
                        {a.field && (
                          <span className="text-muted-foreground">
                            {FIELD_LABELS[a.field] || a.field}
                          </span>
                        )}
                      </div>
                      {(a.old_value || a.new_value) && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {a.old_value && <span className="line-through">{a.old_value}</span>}
                          {a.old_value && a.new_value && ' → '}
                          {a.new_value && <span className="font-medium text-foreground">{a.new_value}</span>}
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {new Date(a.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
